import React from 'react';
import { createPortal } from 'react-dom';
import { FaUndo, FaRedo, FaHeadphones, FaPlay, FaStop, FaTrash, FaVolumeUp } from "react-icons/fa";
import { DEGREE_TABLE_SHARP, DEGREE_TABLE_FLAT } from "../data/degreeTables";
import { analyzeChordType } from "../utils/ChordAnalyzeLogic";
import ChordTypeLabel from "./ChordTypeLabel";
import "../styles/ScoreSheet.css";
import { useChordAudio } from '../hooks/useChordAudio';
import useLocale from '../hooks/useLocale';

const BASE_CELL_WIDTH = 80;
const BASE_SECTION_WIDTH = 82;
const BASE_REMOVE_WIDTH = 52;
const BASE_CELL_HEIGHT = 40;
const CHORD_QUALITIES = [
  { label: 'M7', value: 'M7' },
  { label: 'dim', value: 'dim' },
  { label: 'm7', value: 'm7' },
  { label: 'm', value: 'm' },
  { label: 'sus4', value: 'sus4' },
  { label: '(b5)', value: '(b5)' },
  { label: 'aug', value: 'aug' },
  { label: 'add9', value: 'add9' },
  { label: '7', value: '7' },
  { label: '9', value: '9' },
  { label: '(b9)', value: '(b9)' },
  { label: '(#9)', value: '(#9)' },
  { label: '(11)', value: '(11)' },
  { label: '(#11)', value: '(#11)' },
  { label: '(13)', value: '(13)' },
  { label: '(b13)', value: '(b13)' },
];
const ROOT_NOTES = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const ROOT_ACCIDENTALS = [
  { label: '#', value: '#' },
  { label: 'b', value: 'b' },
];

const TO_SHARP_EQUIV = {
  Db: 'C#',
  Eb: 'D#',
  Gb: 'F#',
  Ab: 'G#',
  Bb: 'A#',
  Cb: 'B',
  Fb: 'E',
};

const TO_FLAT_EQUIV = {
  'C#': 'Db',
  'D#': 'Eb',
  'F#': 'Gb',
  'G#': 'Ab',
  'A#': 'Bb',
  'B#': 'C',
  'E#': 'F',
};

const ScoreSheet = ({
  partIndex = 0,
  selectedKey,
  rows = [],
  onRowsChange,
  activeCell: sequencerCell = null,
  onActiveInputCellChange,
  onRowKeySelect,
  onRowKeyClear,
  showSequenceControl = false,
  isSequencePlaying = false,
  onToggleSequence,
  sequenceProgress = { current: 0, total: 0 },
  tempo = 120,
}) => {
  const locale = useLocale();
  const text = locale === 'ja'
    ? { inputChord: 'コード入力', empty: '未入力', addRow: '行を追加', play: 'スコアを再生', stop: '再生を停止' }
    : { inputChord: 'Input Chord', empty: 'empty', addRow: 'Add row', play: 'Play score', stop: 'Stop score' };
  const getDegreeForNote = React.useCallback(
    (note, key = selectedKey) => {
      if (!note) return "";
      const degreeTable = DEGREE_TABLE_SHARP[key] || DEGREE_TABLE_FLAT[key] || {};
      const preferredNote = DEGREE_TABLE_SHARP[key]
        ? TO_SHARP_EQUIV[note] || note
        : TO_FLAT_EQUIV[note] || note;
      return degreeTable[note] || degreeTable[preferredNote] || "";
    },
    [selectedKey]
  );
  const sequencePercent = sequenceProgress.total
    ? Math.round((sequenceProgress.current / sequenceProgress.total) * 100)
    : 0;

  // Shift選択や矢印移動などで使う、選択中のセル範囲（start/end座標）
  const [selectedRange, setSelectedRange] = React.useState(null);
  const [activeInputCell, setActiveInputCell] = React.useState(null);
  const [assistPosition, setAssistPosition] = React.useState(null);
  const [interactionMode, setInteractionMode] = React.useState("nav");
  const undoStackRef = React.useRef([]);
  const redoStackRef = React.useRef([]);

  // セルが現在選択中の範囲内にあるかを判定
  const isCellInRange = (row, col) => {
  if (!selectedRange) return false;
  const [startRow, startCol] = selectedRange.start;
  const [endRow, endCol] = selectedRange.end;
  return (
      row >= Math.min(startRow, endRow) &&
      row <= Math.max(startRow, endRow) &&
      col >= Math.min(startCol, endCol) &&
      col <= Math.max(startCol, endCol)
    );
  };

  const getRangeBounds = React.useCallback((range) => {
    if (!range) return null;
    const { start, end } = range;
    return {
      startRow: Math.min(start[0], end[0]),
      endRow: Math.max(start[0], end[0]),
      startCol: Math.min(start[1], end[1]),
      endCol: Math.max(start[1], end[1]),
    };
  }, []);

  const [isCompact, setIsCompact] = React.useState(false);
  const scrollRef = React.useRef(null);
  const sheetRef = React.useRef(null);
  const assistRef = React.useRef(null);
  const [layoutWidths, setLayoutWidths] = React.useState({
    cell: BASE_CELL_WIDTH,
    section: BASE_SECTION_WIDTH,
    remove: BASE_REMOVE_WIDTH,
  });
  const { playChord } = useChordAudio({ instrument: 'piano' });

  React.useEffect(() => {
    const handleResize = () => {
      if (typeof window === "undefined") return;
      setIsCompact(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (isCompact) {
      setLayoutWidths({ cell: 52, section: 72, remove: 24 });
      return;
    }
    const numCols = rows[0]?.chords.length || 8;
    const containerWidth = scrollRef.current?.clientWidth || 0;
    if (!containerWidth) return;
    const desiredTotal =
      BASE_SECTION_WIDTH + (numCols * BASE_CELL_WIDTH) + BASE_REMOVE_WIDTH;
    if (desiredTotal <= containerWidth) {
      setLayoutWidths({
        cell: BASE_CELL_WIDTH,
        section: BASE_SECTION_WIDTH,
        remove: BASE_REMOVE_WIDTH,
      });
      return;
    }
    const scale = containerWidth / desiredTotal;
    const sectionWidth = Math.max(80, Math.floor(BASE_SECTION_WIDTH * scale));
    const removeWidth = Math.max(24, Math.floor(BASE_REMOVE_WIDTH * scale));
    const cellWidth = Math.max(
      36,
      Math.floor((containerWidth - sectionWidth - removeWidth) / numCols)
    );
    setLayoutWidths({ cell: cellWidth, section: sectionWidth, remove: removeWidth });
  }, [isCompact, rows]);

  const cellWidth = layoutWidths.cell;
  const sectionWidth = layoutWidths.section;
  const removeWidth = layoutWidths.remove;
  const cellHeight = isCompact ? 36 : BASE_CELL_HEIGHT;

  const pushUndoSnapshot = React.useCallback(() => {
    undoStackRef.current.push(JSON.stringify(rows));
    if (undoStackRef.current.length > 50) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
  }, [rows]);

  const handleUndo = React.useCallback(() => {
    if (!undoStackRef.current.length) return;
    const snapshot = undoStackRef.current.pop();
    redoStackRef.current.push(JSON.stringify(rows));
    if (snapshot) {
      onRowsChange(JSON.parse(snapshot));
      setInteractionMode("nav");
    }
  }, [rows, onRowsChange]);

  const handleRedo = React.useCallback(() => {
    if (!redoStackRef.current.length) return;
    const snapshot = redoStackRef.current.pop();
    undoStackRef.current.push(JSON.stringify(rows));
    if (snapshot) {
      onRowsChange(JSON.parse(snapshot));
      setInteractionMode("nav");
    }
  }, [rows, onRowsChange]);

  const clearSelectedCells = React.useCallback((range) => {
    const bounds = getRangeBounds(range);
    if (!bounds) return;
    const { startRow, endRow, startCol, endCol } = bounds;
    pushUndoSnapshot();
    const updated = rows.map((row, rowIdx) => {
      if (rowIdx < startRow || rowIdx > endRow) return row;
      const nextRow = { ...row, chords: [...row.chords] };
      for (let col = startCol; col <= endCol; col += 1) {
        if (col >= 0 && col < nextRow.chords.length) {
          nextRow.chords[col] = '';
        }
      }
      return nextRow;
    });
    onRowsChange(updated);
  }, [getRangeBounds, onRowsChange, pushUndoSnapshot, rows]);

  const focusInput = React.useCallback((rowIdx, colIdx, selectEnd = false) => {
    requestAnimationFrame(() => {
      const nextInput = document.querySelector(`#input-${partIndex}-${rowIdx}-${colIdx}`);
      if (nextInput) {
        nextInput.focus();
        if (selectEnd) {
          const len = nextInput.value.length;
          nextInput.setSelectionRange(len, len);
        }
      }
    });
  }, [partIndex]);

  const applyChordQuality = React.useCallback((quality) => {
    if (!activeInputCell) return;
    const [rowIdx, colIdx] = activeInputCell;
    const row = rows[rowIdx];
    if (!row || !row.chords) return;
    pushUndoSnapshot();
    const updated = [...rows];
    const nextRow = { ...row, chords: [...row.chords] };
    const current = nextRow.chords[colIdx] || '';
    nextRow.chords[colIdx] = `${current}${quality}`;
    updated[rowIdx] = nextRow;
    onRowsChange(updated);
    setInteractionMode("edit");
    focusInput(rowIdx, colIdx, true);
  }, [activeInputCell, rows, onRowsChange, focusInput, pushUndoSnapshot]);

  const applyRootNote = React.useCallback((root) => {
    if (!activeInputCell) return;
    const [rowIdx, colIdx] = activeInputCell;
    const row = rows[rowIdx];
    if (!row || !row.chords) return;
    pushUndoSnapshot();
    const updated = [...rows];
    const nextRow = { ...row, chords: [...row.chords] };
    const current = nextRow.chords[colIdx] || '';
    const suffix = current.replace(/^[A-G](?:#|b)?/, '');
    nextRow.chords[colIdx] = `${root}${suffix}`;
    updated[rowIdx] = nextRow;
    onRowsChange(updated);
    setInteractionMode("edit");
    focusInput(rowIdx, colIdx, true);
  }, [activeInputCell, rows, onRowsChange, focusInput, pushUndoSnapshot]);

  const applyRootAccidental = React.useCallback((accidental) => {
    if (!activeInputCell) return;
    const [rowIdx, colIdx] = activeInputCell;
    const row = rows[rowIdx];
    if (!row || !row.chords) return;
    const current = row.chords[colIdx] || '';
    const match = current.match(/^([A-G])(?:#|b)?(.*)$/);
    let nextValue = current;

    if (match) {
      nextValue = `${match[1]}${accidental}${match[2]}`;
    } else if (!current.trim()) {
      nextValue = `C${accidental}`;
    } else {
      return;
    }

    pushUndoSnapshot();
    const updated = [...rows];
    const nextRow = { ...row, chords: [...row.chords] };
    nextRow.chords[colIdx] = nextValue;
    updated[rowIdx] = nextRow;
    onRowsChange(updated);
    setInteractionMode("edit");
    focusInput(rowIdx, colIdx, true);
  }, [activeInputCell, rows, onRowsChange, focusInput, pushUndoSnapshot]);

  const updateAssistPosition = React.useCallback(() => {
    if (!activeInputCell) { setAssistPosition(null); return; }
    const [rowIdx, colIdx] = activeInputCell;
    const input = sheetRef.current?.querySelector(`#input-${partIndex}-${rowIdx}-${colIdx}`);
    if (!input) { setAssistPosition(null); return; }
    const rect = input.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
    const viewportLeft = viewport?.offsetLeft || 0;
    const viewportWidth = viewport?.width || window.innerWidth;
    const gap = 8;
    if (rect.bottom < viewportTop + 60 || rect.top > viewportBottom || rect.right < viewportLeft || rect.left > viewportLeft + viewportWidth) {
      setAssistPosition(null);
      return;
    }
    const width = Math.min(344, viewportWidth - gap * 2);
    const below = viewportBottom - rect.bottom - gap * 2;
    const above = rect.top - Math.max(viewportTop, 60) - gap * 2;
    const placeBelow = below >= 250 || below >= above;
    const maxHeight = Math.max(0, placeBelow ? below : above);
    setAssistPosition({
      left: Math.max(viewportLeft + gap, Math.min(rect.left, viewportLeft + viewportWidth - width - gap)),
      top: placeBelow ? rect.bottom + gap : undefined,
      bottom: placeBelow ? undefined : window.innerHeight - rect.top + gap,
      width,
      maxHeight,
    });
  }, [activeInputCell, partIndex]);

  React.useLayoutEffect(() => {
    updateAssistPosition();
    const observer = new ResizeObserver(updateAssistPosition);
    if (sheetRef.current) observer.observe(sheetRef.current);
    window.addEventListener('scroll', updateAssistPosition, true);
    window.addEventListener('resize', updateAssistPosition);
    window.visualViewport?.addEventListener('resize', updateAssistPosition);
    window.visualViewport?.addEventListener('scroll', updateAssistPosition);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateAssistPosition, true);
      window.removeEventListener('resize', updateAssistPosition);
      window.visualViewport?.removeEventListener('resize', updateAssistPosition);
      window.visualViewport?.removeEventListener('scroll', updateAssistPosition);
    };
  }, [updateAssistPosition]);

  React.useEffect(() => {
    const handleDocClick = (event) => {
      if (!sheetRef.current) return;
      if (sheetRef.current.contains(event.target) || assistRef.current?.contains(event.target)) return;
      setSelectedRange(null);
      setActiveInputCell(null);
      setAssistPosition(null);
      setInteractionMode("nav");
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  // 行を削除／ラベル変更／コード変更／行挿入 の各ハンドラ
  const handleRemoveRow = (index) => {
    pushUndoSnapshot();
    const newRows = [...rows];
    newRows.splice(index, 1);
    onRowsChange(newRows);
  };

  const handleLabelChange = (value, index) => {
    const updated = rows.map((row, rowIndex) => rowIndex === index ? { ...row, label: value } : row);
    onRowsChange(updated);
  };

  const handleChordsChange = (chords, index) => {
    pushUndoSnapshot();
    const updated = rows.map((row, rowIndex) => rowIndex === index ? { ...row, chords } : row);
    onRowsChange(updated);
  };

  const handleLyricsChange = (value, rowIndex, colIndex) => {
    const updated = [...rows];
    const lyrics = [...(updated[rowIndex].lyrics || Array(updated[rowIndex].chords.length).fill(''))];
    lyrics[colIndex] = value;
    updated[rowIndex] = { ...updated[rowIndex], lyrics };
    onRowsChange(updated);
  };

  const handleInsertRow = (index) => {
    pushUndoSnapshot();
    const numCols = rows[0]?.chords.length || 8;
    const newRows = [...rows];
    newRows.splice(index + 1, 0, { label: '', chords: Array(numCols).fill('') });
    onRowsChange(newRows);
  };

  // Ctrl/Cmd + C/V によるセル範囲のコピー＆ペーストをサポート
  React.useEffect(() => {
    const handleCopy = (e) => {
      const targetRange = selectedRange || (activeInputCell ? { start: activeInputCell, end: activeInputCell } : null);
      const bounds = getRangeBounds(targetRange);
      if (!bounds) return;
      const { startRow, endRow, startCol, endCol } = bounds;

      const copied = rows.slice(startRow, endRow + 1).map(row =>
        row.chords.slice(startCol, endCol + 1).join('\t')
      ).join('\n');

      e.clipboardData.setData('text/plain', copied);
      e.preventDefault();
    };

    const handlePaste = (e) => {
      const anchor = selectedRange?.start || activeInputCell;
      if (!anchor) return;
      const startRow = anchor[0];
      const startCol = anchor[1];

      const text = e.clipboardData.getData('text/plain');
      if (!text) return;

      const normalized = text.replace(/\r/g, '');
      const lines = normalized
        .split('\n')
        .filter((line, index, arr) => !(index === arr.length - 1 && line === ''))
        .map((line) => line.split('\t'));
      pushUndoSnapshot();
      const newRows = rows.map((row) => ({ ...row, chords: [...row.chords] }));

      lines.forEach((line, rIdx) => {
        const rowIndex = startRow + rIdx;
        if (!newRows[rowIndex]) return;
        line.forEach((cell, cIdx) => {
          const colIndex = startCol + cIdx;
          if (colIndex < newRows[rowIndex].chords.length) {
            newRows[rowIndex].chords[colIndex] = cell;
          }
        });
      });

      onRowsChange(newRows);
      const endRow = Math.min(startRow + lines.length - 1, rows.length - 1);
      const endCol = Math.min(
        startCol + Math.max(...lines.map((line) => line.length)) - 1,
        (rows[0]?.chords?.length || 1) - 1
      );
      setSelectedRange({ start: [startRow, startCol], end: [endRow, endCol] });
      setActiveInputCell([startRow, startCol]);
      e.preventDefault();
    };

    const handleCut = (e) => {
      const targetRange = selectedRange || (activeInputCell ? { start: activeInputCell, end: activeInputCell } : null);
      const bounds = getRangeBounds(targetRange);
      if (!bounds) return;
      const { startRow, endRow, startCol, endCol } = bounds;
      const copied = rows.slice(startRow, endRow + 1).map(row =>
        row.chords.slice(startCol, endCol + 1).join('\t')
      ).join('\n');
      e.clipboardData.setData('text/plain', copied);
      clearSelectedCells(targetRange);
      e.preventDefault();
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [selectedRange, activeInputCell, rows, onRowsChange, getRangeBounds, pushUndoSnapshot, clearSelectedCells]);

  const isActiveCell = (rowIdx, colIdx) =>
    activeInputCell && activeInputCell[0] === rowIdx && activeInputCell[1] === colIdx;

  const handleCellClick = (rowIdx, colIdx, e) => {
    if (e.shiftKey && selectedRange) {
      setSelectedRange({ start: selectedRange.start, end: [rowIdx, colIdx] });
      setInteractionMode("nav");
    } else {
      setSelectedRange({ start: [rowIdx, colIdx], end: [rowIdx, colIdx] });
      setInteractionMode("edit");
    }
    setActiveInputCell([rowIdx, colIdx]);
  };

  const handleCellKeyDown = (e, rowIdx, colIdx, row) => {
    const numCols = row.chords.length;
    const numRows = rows.length;
    const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
    const shortcut = e.ctrlKey || e.metaKey;

    if (shortcut && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      setSelectedRange({ start: [0, 0], end: [numRows - 1, numCols - 1] });
      setActiveInputCell([0, 0]);
      setInteractionMode("nav");
      focusInput(0, 0);
      return;
    }

    if (shortcut && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) {
        handleRedo();
      } else {
        handleUndo();
      }
      return;
    }

    if (shortcut && e.key.toLowerCase() === 'y') {
      e.preventDefault();
      handleRedo();
      return;
    }

    if ((e.key === "Backspace" || e.key === "Delete") && selectedRange) {
      const bounds = getRangeBounds(selectedRange);
      if (bounds && (bounds.startRow !== bounds.endRow || bounds.startCol !== bounds.endCol)) {
        e.preventDefault();
        clearSelectedCells(selectedRange);
        setInteractionMode("nav");
        focusInput(rowIdx, colIdx);
        return;
      }
    }

    if (interactionMode === "nav") {
      if (printable) {
        e.preventDefault();
        const newChords = [...row.chords];
        newChords[colIdx] = e.key;
        handleChordsChange(newChords, rowIdx);
        setInteractionMode("edit");
        setActiveInputCell([rowIdx, colIdx]);
        setSelectedRange({ start: [rowIdx, colIdx], end: [rowIdx, colIdx] });
        focusInput(rowIdx, colIdx, true);
        return;
      }
    }

    let newRow = rowIdx;
    let newCol = colIdx;
    const cursorPos = e.target.selectionStart;
    const textLength = e.target.value.length;

    if (e.key === "Tab") {
      e.preventDefault();
      const direction = e.shiftKey ? -1 : 1;
      let nextRow = rowIdx;
      let nextCol = colIdx + direction;
      if (nextCol >= numCols) {
        nextCol = 0;
        nextRow = Math.min(rowIdx + 1, numRows - 1);
      } else if (nextCol < 0) {
        nextCol = numCols - 1;
        nextRow = Math.max(rowIdx - 1, 0);
      }
      setSelectedRange({ start: [nextRow, nextCol], end: [nextRow, nextCol] });
      setActiveInputCell([nextRow, nextCol]);
      setInteractionMode("nav");
      focusInput(nextRow, nextCol);
      return;
    } else if (e.key === "ArrowRight" && cursorPos === textLength) {
      newCol = (colIdx + 1) % numCols;
      if (newCol === 0 && rowIdx < numRows - 1) newRow++;
    } else if (e.key === "ArrowLeft" && cursorPos === 0) {
      newCol = (colIdx - 1 + numCols) % numCols;
      if (newCol === numCols - 1 && rowIdx > 0) newRow--;
    } else if (e.key === "ArrowDown") {
      if (rowIdx < numRows - 1) newRow++;
    } else if (e.key === "ArrowUp") {
      if (rowIdx > 0) newRow--;
    } else if (e.key === "Enter") {
      e.preventDefault();
      const nextRow = Math.min(rowIdx + 1, numRows - 1);
      setActiveInputCell([nextRow, colIdx]);
      setSelectedRange({ start: [nextRow, colIdx], end: [nextRow, colIdx] });
      focusInput(nextRow, colIdx);
      return;
    } else if (e.key === "Escape") {
      e.preventDefault();
      setInteractionMode("nav");
      focusInput(rowIdx, colIdx);
      return;
    }

    if (newRow !== rowIdx || newCol !== colIdx) {
      e.preventDefault();
      if (e.shiftKey && selectedRange) {
        setSelectedRange({ start: selectedRange.start, end: [newRow, newCol] });
        setInteractionMode("nav");
      } else {
        setSelectedRange({ start: [newRow, newCol], end: [newRow, newCol] });
      }
      setActiveInputCell([newRow, newCol]);
      focusInput(newRow, newCol);
    }
  };

  React.useEffect(() => {
    if (!activeInputCell) return;
    const [rowIdx, colIdx] = activeInputCell;
    if (interactionMode === "edit") {
      focusInput(rowIdx, colIdx, true);
    }
  }, [activeInputCell, interactionMode, focusInput]);

  React.useEffect(() => {
    onActiveInputCellChange?.(partIndex, activeInputCell);
  }, [activeInputCell, onActiveInputCellChange, partIndex]);

  const handlePlayChord = React.useCallback(
    (symbol) => {
      if (!symbol || !symbol.trim()) return;
      playChord(symbol);
    },
    [playChord]
  );

  // ===== JSXによるテーブル表示部分 =====
  return (
    <div
      // テーブル外クリックで選択セルを解除
      onClick={(e) => {
        if (e.target.tagName !== "INPUT") {
          setSelectedRange(null);
        }
      }}
    >
      {/* コード入力テーブル */}
      <div className="score-sheet-shell" ref={sheetRef}>
        {activeInputCell && createPortal(
          <div
            ref={assistRef}
            className="chord-assist-float"
            role="region"
            aria-label={text.inputChord}
            onMouseDown={(event) => event.preventDefault()}
            style={{ ...assistPosition, visibility: assistPosition ? 'visible' : 'hidden' }}
          >
            <div className="chord-assist-header">
              <span className="chord-assist-title">{text.inputChord}</span>
              <span className="chord-assist-value">
                {rows[activeInputCell[0]]?.chords?.[activeInputCell[1]] || `(${text.empty})`}
              </span>
            </div>
            <div className="chord-assist-buttons chord-assist-buttons--root" role="group" aria-label="Root notes">
              {ROOT_NOTES.map((root) => (
                <button
                  key={root}
                  type="button"
                  className="chord-assist-button chord-assist-button--root"
                  onClick={() => applyRootNote(root)}
                >
                  {root}
                </button>
              ))}
            </div>
            <div className="chord-assist-buttons chord-assist-buttons--accidentals" role="group" aria-label="Root accidentals">
              {ROOT_ACCIDENTALS.map((accidental) => (
                <button
                  key={accidental.value}
                  type="button"
                  className="chord-assist-button chord-assist-button--accidental"
                  onClick={() => applyRootAccidental(accidental.value)}
                >
                  {accidental.label}
                </button>
              ))}
            </div>
            <div className="chord-assist-separator" aria-hidden="true" />
            <div className="chord-assist-buttons chord-assist-buttons--qualities" role="group" aria-label="Chord input assist">
              {CHORD_QUALITIES.map((quality) => (
                <button
                  key={quality.value}
                  type="button"
                  className="chord-assist-button"
                  onClick={() => applyChordQuality(quality.value)}
                >
                  {quality.label}
                </button>
              ))}
            </div>
          </div>, document.body
        )}
        <div className="score-sheet-scroll mt-5" ref={scrollRef}>
        <div className="score-sheet-inner">
          <div className="score-toolbar">
            <div className="score-toolbar-actions">
              <button
                type="button"
                className="history-button"
                onClick={handleUndo}
                disabled={!undoStackRef.current.length}
                aria-label="Undo"
              >
                <FaUndo className="history-icon" aria-hidden="true" />
              </button>
              <span className="toolbar-divider" />
              <button
                type="button"
                className="history-button"
                onClick={handleRedo}
                disabled={!redoStackRef.current.length}
                aria-label="Redo"
              >
                <FaRedo className="history-icon" aria-hidden="true" />
              </button>
            </div>
          </div>
          <table className="chord-table">
            <tbody>
              {/* 各行データをレンダリング（セクション名・ディグリー・コード入力） */}
              {rows.map((row, rowIdx) => (
                <React.Fragment key={rowIdx}>
                  <tr className="hover-row">
                    <td
                      style={{ width: `${removeWidth}px`, height: `${cellHeight * 2}px`, textAlign: 'center', verticalAlign: 'middle' }}
                      rowSpan={2}
                      >
                        <div className="score-row-index">
                          {rows.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveRow(rowIdx)}
                              className="remove-row-button"
                              aria-label={`Remove row ${rowIdx + 1}`}
                            >
                              <FaTrash aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                    <th
                      style={{ width: `${sectionWidth}px`, height: `${cellHeight}px` }}
                      className="text-center align-middle section-header"
                      rowSpan={2}
                    >
                      <div className="score-row-labels">
                        <div className="row-key-control">
                          {row.key ? (
                            <>
                              <button
                                type="button"
                                className="row-key-trigger"
                                onClick={() => onRowKeySelect?.(rowIdx)}
                              >
                                {`key: ${row.key}`}
                              </button>
                              <button
                                type="button"
                                className="row-key-clear"
                                onClick={() => onRowKeyClear?.(rowIdx)}
                                aria-label={`Clear key override for row ${rowIdx + 1}`}
                              >
                                ×
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="row-key-trigger row-key-trigger--base"
                              onClick={() => onRowKeySelect?.(rowIdx)}
                            >
                              {`key: ${selectedKey}`}
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          className="section-label-input"
                          style={{ minHeight: `${cellHeight}px` }}
                          value={row.label}
                          aria-label={`Section label for row ${rowIdx + 1}`}
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          onChange={(e) => handleLabelChange(e.target.value, rowIdx)}
                        />
                      </div>
                    </th>
                    {row.chords.map((chord, idx) => {
                      const effectiveKey = row.key || selectedKey;
                      const degreeLabel = (() => {
                        if (chord.includes('/')) {
                          const [upper, bass] = chord.split('/');
                          const upperBase = upper.match(/^([A-G][#b]?)/)?.[1] || "";
                          const bassBase = bass.match(/^([A-G][#b]?)/)?.[1] || "";
                          const upperDegree = getDegreeForNote(upperBase, effectiveKey);
                          const bassDegree = getDegreeForNote(bassBase, effectiveKey);
                          return `${upperDegree}/${bassDegree}`;
                        } else {
                          const base = chord.match(/^([A-G][#b]?)/)?.[1] || "";
                          return getDegreeForNote(base, effectiveKey);
                        }
                      })();
                      // Determine degree classes for coloring
                      const degreeClasses = [];
                      if (degreeLabel === 'I') degreeClasses.push('degree-I');
                      if (degreeLabel === 'II') degreeClasses.push('degree-II');
                      if (degreeLabel === 'III') degreeClasses.push('degree-III');
                      if (degreeLabel === 'IV') degreeClasses.push('degree-IV');
                      if (degreeLabel === 'V') degreeClasses.push('degree-V');
                      if (degreeLabel === 'VI') degreeClasses.push('degree-VI');
                      if (degreeLabel === 'VII') degreeClasses.push('degree-VII');

                      return (
                        <th
                          key={idx}
                          style={{ width: `${cellWidth}px`, height: `${cellHeight}px` }}
                          className={`text-center align-middle ${degreeClasses.join(' ')}`}
                        >
                          <input
                            type="text"
                            className="lyrics-input"
                            value={row.lyrics?.[idx] || ''}
                            aria-label={`Lyrics for row ${rowIdx + 1}, cell ${idx + 1}`}
                            onChange={(event) => handleLyricsChange(event.target.value, rowIdx, idx)}
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          />
                          <span className="degree-label">{degreeLabel}</span>
                        </th>
                      );
                    })}
                  </tr>
                  <tr>
                    {row.chords.map((chord, idx) => {
                      const selected = isCellInRange(rowIdx, idx);
                      const isActive = isActiveCell(rowIdx, idx);
                      const isEditing = interactionMode === "edit" && isActive;
                      const isPlaying =
                        sequencerCell &&
                        sequencerCell.rowIndex === rowIdx &&
                        sequencerCell.colIndex === idx;
                      const inputClass = `score-input${selected ? ' score-input--selected' : ''}${isActive ? ' score-input--active' : ''}${isEditing ? ' score-input--editing' : ''}${isPlaying ? ' score-input--playing' : ''}`;
                      return (
                        <td
                          key={idx}
                          style={{ width: `${cellWidth}px`, height: `${cellHeight}px`, position: 'relative' }}
                          className="text-center align-middle chord-cell"
                        >
                          <input
                            // 矢印キーでセル間移動（選択セルも更新）
                            id={`input-${partIndex}-${rowIdx}-${idx}`}
                            type="text"
                            name={`chord-cell-${partIndex}-${rowIdx}-${idx}`}
                            value={chord}
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            onChange={(e) => {
                              const newChords = [...row.chords];
                              newChords[idx] = e.target.value;
                              handleChordsChange(newChords, rowIdx);
                            }}
                            onKeyDown={(e) => handleCellKeyDown(e, rowIdx, idx, row)}
                            onClick={(e) => handleCellClick(rowIdx, idx, e)}
                            className={inputClass}
                            style={{ width: '100%', height: '100%', boxSizing: 'border-box' }}
                          />
                          {chord?.trim() && (
                            <button
                              type="button"
                              className="chord-play-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayChord(chord);
                              }}
                              aria-label="Play chord"
                            >
                              ▶
                            </button>
                          )}
                          <div
                            style={{
                              position: 'absolute',
                              top: '2px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              zIndex: 10,
                            }}
                          >
                            <ChordTypeLabel type={analyzeChordType(chord, row.key || selectedKey)} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="insert-row">
                    <td colSpan={(rows[0]?.chords?.length || 8) + 2} className="text-center">
                      <button
                        type="button"
                        className="insert-row-button"
                        onClick={() => handleInsertRow(rowIdx)}
                        aria-label={text.addRow}
                      >
                        <span aria-hidden="true">＋</span> {text.addRow}
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {showSequenceControl && (
            <div className="score-transport" aria-label="Score playback controls">
              <button
                type="button"
                className="score-transport-play"
                onClick={onToggleSequence}
                aria-label={isSequencePlaying ? text.stop : text.play}
              >
                {isSequencePlaying ? <FaStop /> : <FaPlay />}
              </button>
              <div className="score-transport-progress">
                <div className="score-transport-track" aria-hidden="true">
                  <span style={{ width: `${sequencePercent}%` }} />
                </div>
                <span>0:00</span>
              </div>
              <div className="score-transport-actions">
                <FaVolumeUp aria-hidden="true" />
                <FaHeadphones aria-hidden="true" />
                <span>{tempo} BPM</span>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreSheet;
